import React, { useCallback, useState } from "react";
import styles from "./OrderDetails.module.scss";
import footerStyles from "./OrderDetails.module.scss";
import {
  AdSpot,
  AdState,
  ApiAdRecord,
} from "../../../../core/orderAd/orderAd.entity";
import { adStateLabels } from "../ordersList/tabs/adOrders/orderedAdItem/OrderedAdItem";
import { strings } from "../../../../intl/fa";
import { formatStrings } from "../../../../utils/utils";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { useTheme } from "@emotion/react";
import { Button, ButtonSize, Copy, InfoSquare } from "@bale/ui";
import Footer from "../../../commons/footer/Footer";
import { useRequest } from "../../../../hooks/useRequest";
import { getPaymentTokenThunk } from "../../../../core/orderAd/orderAd.thunk";
import { GetAdPriceResponse } from "core/adPrice/adPrice.api";
import { toast } from "react-toastify";
import { copyTextToClipboard } from "utils/clipboard";
import BonusCodeInputModal from "components/modals/bonusCodeInputModal/BonusCodeInputModal";
import { ApiBonusCodeRecord } from "core/code/code.entity";
import { useAppDispatch, useAppSelector } from "utils/store";
import { NavigationActions, AppPage } from "core/navigation/navigation.slice";
import { MIN_ORDER_PRICE_FOR_INVOICE } from "utils/auth";
import { safeRedirect } from "utils/payment";
import { useNeedAuth } from "hooks/useNeedAuth";
import { epochToFullDateTime } from "utils/time";
import { useMyPanel } from "hooks/useMyPanel";
import { AccountType } from "core/auth/auth.entity";

export const Row: React.FC<
  React.PropsWithChildren<{
    rowKey: string;
    value: string | React.ReactNode;
    dir?: "ltr" | "rtl";
  }>
> = ({ rowKey, value, dir }) => {
  return (
    <div className={styles.Row}>
      <p className={styles.Key}>{rowKey}</p>
      <span className={styles.Value} style={{ direction: dir ?? "rtl" }}>
        {value}
      </span>
    </div>
  );
};

const getIcon = (state: AdState) => {
  const base = "/icons/adState/";
  switch (state) {
    case AdState.AwaitingContentApproval:
      return base + "wait_to_approve.svg";
    case AdState.AwaitingPayment:
      return base + "payment.svg";
    case AdState.AwaitingToShow:
      return base + "waiting.svg";
    case AdState.Active:
      return base + "viewing.svg";
    case AdState.Finished:
      return base + "done.svg";
    case AdState.Canceled:
    case AdState.Rejected:
      return base + "rejected.svg";
  }
};

export type AdRecordProps = {
  adRecord: ApiAdRecord & { bonusView?: number; bonusClick?: number };
};
const DetailCard: React.FC<React.PropsWithChildren<AdRecordProps>> = ({
  adRecord,
}) => {
  const theme = useTheme();

  function adIdCopyHandler() {
    toast.info(
      formatStrings(strings.order_details.orderIdCopied, { value: strings.ad })
    );
    copyTextToClipboard(adRecord.id);
  }

  const bonusView = adRecord.bonusView || 0;
  const bonusClick = adRecord.bonusClick || 0;
  const isUsingBonusCode = bonusView | bonusClick;

  return (
    <div className={styles.Card}>
      <div className={styles.HeaderWrapper}>
        <div className={styles.LabelWrapper}>
          <img src={getIcon(adRecord.state)} alt="" />
          <p className={styles.Label}>{adStateLabels[adRecord.state]?.label}</p>
        </div>
        <div className={styles.CopyWrapper} onClick={adIdCopyHandler}>
          <Copy size={20} color={theme.colors.primary[50]} />
          <p>{strings.order_details.ad_id}</p>
        </div>
      </div>
      <div className={styles.InfoWrapper}>
        {adRecord.state === AdState.Rejected && (
          <div className={styles.rejectionReason}>
            {formatStrings(strings.order_details.rejection_message, {
              reason: adRecord.rejection_reason,
            })}
          </div>
        )}
        {adRecord.spot === AdSpot.AdSpotDialog && (
          <Row rowKey={strings.order_details.title} value={adRecord.title} />
        )}
        <Row
          rowKey={strings.order_details.description}
          value={adRecord.description}
        />
        {/* {adRecord.spot === AdSpot.AdSpotSponsoredMessage && (
          <Row
            rowKey={strings.order_details.member_count_option}
            value={sponsoredMessageOptionToText(
              adRecord.sponsored_message_options
            )}
          />
        )} */}
        {adRecord.view_count > 0 && (
          <Row
            rowKey={strings.order_details.view_count}
            value={
              <div className={styles.DataCount}>
                {digitsToHindi(
                  toMoneyFormat(
                    String(
                      isUsingBonusCode ? adRecord.view_count : adRecord.set_view
                    )
                  )
                )}
                {bonusView > 0 &&
                  adRecord.spot !== AdSpot.AdSpotSponsoredMessage && (
                    <div className={styles.Bonus}>
                      {" + " + digitsToHindi(toMoneyFormat(String(bonusView)))}
                    </div>
                  )}
              </div>
            }
            dir={"ltr"}
          />
        )}
        {adRecord.click_count > 0 && (
          <Row
            rowKey={strings.order_details.click_count}
            value={
              <div className={styles.DataCount}>
                {digitsToHindi(
                  toMoneyFormat(
                    String(
                      isUsingBonusCode
                        ? adRecord.click_count
                        : adRecord.set_click
                    )
                  )
                )}
                {bonusClick > 0 && (
                  <div className={styles.Bonus}>
                    {" + " + digitsToHindi(toMoneyFormat(String(bonusClick)))}
                  </div>
                )}
              </div>
            }
            dir={"ltr"}
          />
        )}
        {adRecord.state === AdState.AwaitingToShow && (
          <Row
            rowKey={strings.order_details.show_date}
            value={digitsToHindi(
              epochToFullDateTime(+new Date(adRecord.desired_start_time))
            )}
            dir={"ltr"}
          />
        )}
      </div>
      {adRecord.state === AdState.AwaitingPayment && (
        <div className={styles.MessageWrapper}>
          <div className={styles.Message}>
            <div className={styles.MessageIcon}>
              <InfoSquare size={20} color={theme.colors.alternative["2"]} />
            </div>
            <p className={styles.MessageText}>
              {strings.order_details.message}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdMainContent: React.FC<React.PropsWithChildren<AdRecordProps>> = (
  props
) => {
  return (
    <div className={styles.Container}>
      <DetailCard adRecord={props.adRecord} />
      {props.children}
    </div>
  );
};

type FooterProps = AdRecordProps & {
  disableButton?: boolean;
  bonusCode?: string;
  adPrice?: GetAdPriceResponse;
  selectedTime?: {
    date: number;
    hour: number;
  };
  adRecord?: ApiAdRecord & { bonusView?: number; bonusClick?: number };
  setBonusCode: (code: ApiBonusCodeRecord) => void;
};
export const PaymentFooter: React.FC<React.PropsWithChildren<FooterProps>> = (
  props
) => {
  const [showBonusCodeInputModal, setShowBonusCodeInputModal] = useState(false);
  const theme = useTheme();
  const { sendRequest: getPaymentToken, loading } =
    useRequest(getPaymentTokenThunk);

  const isDiscountActive = Boolean(
    props.adPrice?.discounted_amount && props.adPrice.discounted_amount > 0
  );
  const totalPriceClasses = `${styles.PriceAmount} ${
    isDiscountActive ? styles.strikethrough : ""
  }`;
  const finalPriceClasses = `${styles.PriceAmount} ${styles.DiscountedPrice}`;
  const dispatch = useAppDispatch();

  const { needsAuth } = useNeedAuth();

  const factorEligible =
    needsAuth &&
    (props.adPrice?.final_price ?? 0) > MIN_ORDER_PRICE_FOR_INVOICE;

  const handleGettingPaymentToken = () => {
    const { onSuccess: safeOnSuccess, onError: safeOnError } = safeRedirect();

    if (props.selectedTime)
      getPaymentToken({
        ad_id: props.adRecord.id,
        bonus_code: props.bonusCode,
        start_date: new Date(
          props.selectedTime.date || Date.now() + 1000000000000
        ).toISOString(),
        start_day: Number(props.selectedTime.hour || 9),
        finalPrice: props.adPrice?.final_price || 0,
        onSuccess: safeOnSuccess,
        onError: safeOnError,
      });
  };
  const myPanel = useMyPanel();
  const isInstant = useAppSelector(
    (state) => state.ordersList.selectedOrder?.is_instant
  );
  const payClickHandler = () => {
    if (factorEligible) {
      // TODO:
      dispatch(NavigationActions.setAppPage(AppPage.Invoicing));
      if (myPanel.type === AccountType.Personal) {
        dispatch(
          NavigationActions.setNavigationCallback(handleGettingPaymentToken)
        );
      }
    } else {
      if (props.adRecord.state === AdState.AwaitingContentApproval) {
        toast.info(strings.order_details.not_approved_yet, {
          toastId: "adTimeNotSelected",
        });
        return;
      }
      if (!isInstant && !props.selectedTime?.date) {
        toast.info(strings.ad_time_select.ad_time_not_selected, {
          toastId: "adTimeNotSelected",
        });
        return;
      }
      if (props.disableButton) {
        return;
      }
      handleGettingPaymentToken();
    }
  };

  const onClickHandler = useCallback(() => {
    if (!props.selectedTime?.date) {
      toast.info(strings.ad_time_select.select_time_first);
      return;
    }
    setShowBonusCodeInputModal(true);
  }, [props.selectedTime?.date]);

  const handleBonusCodeModalClose = useCallback(() => {
    setShowBonusCodeInputModal(false);
  }, []);

  return (
    <>
      <Footer>
        <div className={footerStyles.FooterContent}>
          {props.adRecord.state === AdState.AwaitingPayment && (
            <div className={styles.BonusCodeRow} onClick={onClickHandler}>
              <img src={"/icons/Gift.svg"} alt="gift" />
              <p className={styles.AddBonusCode}>
                {strings.bonus_code_input.adding_bonus_code}
              </p>
            </div>
          )}
          <div className={styles.PriceIndicator}>
            <p className={styles.PriceLabel}>
              {strings.order_details.final_price}
            </p>
            {isDiscountActive && (
              <p className={finalPriceClasses}>
                {digitsToHindi(
                  toMoneyFormat(String(props.adPrice?.final_price)) +
                    " " +
                    strings.common.rial
                )}
              </p>
            )}
            <p className={totalPriceClasses}>
              {props.adPrice?.final_price
                ? digitsToHindi(
                    toMoneyFormat(String(props.adPrice?.total_price)) +
                      " " +
                      strings.common.rial
                  )
                : strings.preview.error_getting_price}
            </p>
          </div>
          <Button
            color={
              factorEligible
                ? theme.colors.primary[40]
                : theme.colors.primary[50]
            }
            text={
              factorEligible
                ? strings.common.button.submit_info
                : strings.common.button.pay
            }
            size={ButtonSize.XLarge}
            onClick={payClickHandler}
            disableThemeOnly={factorEligible ? false : props.disableButton}
            loading={loading}
          />
        </div>
      </Footer>
      {showBonusCodeInputModal && (
        <BonusCodeInputModal
          onClose={handleBonusCodeModalClose}
          adRecord={props.adRecord}
          adPrice={props.adPrice}
          setBonusCode={props.setBonusCode}
        />
      )}
    </>
  );
};
